import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useMutation, useQuery } from '@apollo/client';
import cn from 'classnames';
import { mutationDeleteProject, ProjectStatuses, queryGetCompanies, queryGetProjects, StatusKeys } from '../../query';
import { alphabeticalSorting } from 'Components/Admin/utils';
import { StickyColumnType } from 'Components/Admin/Table/components/TableComponent/TableComponent';
import { ADMIN_URL } from '../../../Admin/constants/constants';

import TrashSVG from 'Components/Admin/assets/TrashSVG';
import EditSVG from 'Components/Admin/assets/EditSVG';
import ShareSVG from 'Components/Admin/assets/ShareSVG';
import PrintSVG from 'Components/Admin/assets/PrintSVG';

import { Link } from 'react-router-dom';
import Table from 'Components/Admin/Table';
import Checkbox from 'Components/Admin/Checkbox';
import ActionPopup from 'Components/Admin/ActionPopup';
import LottieTable from '../../../Employees/components/LottieTable/LottieTable';
import Autocomplete from '../../../../components/Admin/Autocomplete/Autocomplete';
import { useSelectWithQueryOptionsLazy } from 'Shared/hooks/useSelectWithQueryOptions';
import ProjectInfo from '../ProjectInfo';
import useCheckIsMobile from 'Shared/hooks/useCheckIsMobile';
import HeaderSorter from 'Components/Admin/Table/components/HeaderSorter';
import { PERMISSIONS, usePermission } from 'Shared/hooks/usePermission';
import ProjectActions from './components/ProjectActions/ProjectActions';
import getIdIfExist from 'Shared/util/getIdIfExist';

const PROJECTS_PER_PAGE = 10;
const PROJECTS_MIN_COLUMN_WIDTH = 200;

const toastParams = {
  autoClose: 3000,
  closeButton: true,
  hideProgressBar: false,
};

const ProjectsTable = ({ projectsFilter, filterMeta: { isChanged }, onFilterChange }) => {
  const { delete_project, change_project } = PERMISSIONS;
  const isPermission = usePermission();
  const isMobile = useCheckIsMobile();

  const handleError = ({ message }) => {
    toast(message, {
      ...toastParams,
      type: toast.TYPE.ERROR,
    });
  };

  const {
    options: companiesOptions,
    currentOption: currentCompany,
    getDataLazy: getDataLazyCompany,
    refetchLazy: refetchLazyCompany,
  } = useSelectWithQueryOptionsLazy(queryGetCompanies, 'companies', projectsFilter.company);

  const getQueryVariables = ({ status, company, ...variables }) => ({
    ...variables,
    company: getIdIfExist(company),
    state: ProjectStatuses[status],
  });

  const { data: dataProjects, loading } = useQuery(queryGetProjects, {
    fetchPolicy: 'cache-and-network',
    variables: getQueryVariables(projectsFilter),
    onError: handleError,
    //TODO:Remove skip when "new" status will be implemented
    skip: projectsFilter.status === StatusKeys.NEW,
  });

  const handleCompanyChange = (company) => {
    onFilterChange({ company: company || null });
  };

  const renderedProjects = useMemo(() => (dataProjects ? dataProjects.contracts : []), [dataProjects]);

  const [currentProjectsOrder, setCurrentProjectsOrder] = useState([]);
  const [selected, setSelected] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [currentPageSize, setCurrentPageSize] = useState(null);

  const currentPageProjectsIDs = useMemo(() => {
    return currentProjectsOrder.length
      ? currentProjectsOrder.slice(currentPage * currentPageSize, currentPage * currentPageSize + currentPageSize)
      : [];
  }, [currentPage, currentPageSize, currentProjectsOrder]);

  const handleSelectProject = useCallback(({ target: { value, checked } }) => {
    const id = parseInt(value);
    if (checked) {
      setSelected((prevState) => {
        let arr = [...prevState];
        arr.push(id);
        return arr;
      });
    } else {
      setSelected((prevState) => {
        return [...prevState].filter((item) => item !== id);
      });
    }
  }, []);

  const handleAllSelects = ({ target: { checked } }) => {
    if (checked) {
      setSelected(currentPageProjectsIDs);
    } else {
      setSelected([]);
    }
  };

  const handleEverythingSelect = () => setSelected(renderedProjects.map(({ id }) => +id));

  const handleCleanSelects = () => setSelected([]);

  useEffect(() => {
    setSelected([]);
  }, [projectsFilter, currentProjectsOrder]);

  const mapProjectsDataToRows = useCallback(
    (projects) => {
      const filteredProjects = projects
        ? projects.map(({ id, title: projectTitle, reference, company, primaryContact, ...info }) => {
          return {
            select: {
              title: (
                <Checkbox
                  checked={selected.indexOf(+id) !== -1}
                  value={+id}
                  onChange={(e) => handleSelectProject(e)}
                />
              ),
            },
            title: {
              title: (
                <Link to={`/${ADMIN_URL}/projects/${id}/change`} className="table__link">
                  {projectTitle}
                </Link>
              ),
            },
            reference: {
              title: reference,
            },
            company: {
              title: company ? (
                <Link to={`/${ADMIN_URL}/companies/${company.id}/change`} className="table__link">
                  {company.title}
                </Link>
              ) : null,
            },
            contact: {
              title: primaryContact ? (
                <Link to={`/${ADMIN_URL}/contacts/${primaryContact.id}/change`} className="table__link">
                  {`${primaryContact.firstName} ${primaryContact.lastName}`}
                </Link>
              ) : null,
            },
            info: {
              title: <ProjectInfo {...info} />,
            },
            actions: {
              title: <ProjectActions id={id} />,
            },
          };
        })
        : null;

      return filteredProjects && filteredProjects.length
        ? filteredProjects
        : [
          {
            not_found: {
              title: 'No projects with this params',
            },
            title: { title: 'No projects with this params' },
          },
        ];
    },
    [handleSelectProject, selected],
  );

  const isAllSelected = useMemo(() => {
    const idsLength = currentPageProjectsIDs.length;

    return idsLength > 0 && selected.length >= idsLength;
  }, [selected, currentPageProjectsIDs]);

  const columns = [
    {
      cellProps: {
        Header: <Checkbox checked={isAllSelected} onChange={(e) => handleAllSelects(e)} />,
        headerClassName: 'rt-th-select sticky-column sticky-column_left',
        accessor: 'select',
        sortable: false,
        width: 32,
        className: 'sticky-column sticky-column_left',
      },
    },
    {
      cellProps: {
        Header: (
          <HeaderSorter
            selectedCount={isAllSelected && renderedProjects.length > selected.length && renderedProjects.length}
            handleSelectAll={handleEverythingSelect}
          />
        ),
        accessor: 'title',
        sortMethod: alphabeticalSorting,
        minWidth: PROJECTS_MIN_COLUMN_WIDTH,
      },
    },
    {
      title: 'Reference',
      cellProps: {
        accessor: 'reference',
        sortable: false,
        Cell: (props) => {
          return (
            <div className="reference-cell-body">
              <span className="rt-td__head">Reference</span>
              <div className="rt-td__cell">
                <div data-testid="reference">
                  <span className="reference-cell-value">{props.row.reference?.title}</span>
                </div>
              </div>
            </div>
          );
        },
        minWidth: PROJECTS_MIN_COLUMN_WIDTH,
      },
    },
    {
      title: 'Company',
      cellProps: {
        Header: (
          <Autocomplete
            titleCell="Company"
            option={currentCompany}
            options={companiesOptions}
            getItem={handleCompanyChange}
            refetch={refetchLazyCompany}
            onMenuOpen={() => getDataLazyCompany({ term: '' })}
          />
        ),
        accessor: 'company',
        sortable: false,
        minWidth: PROJECTS_MIN_COLUMN_WIDTH,
      },
    },
    {
      title: 'Contact',
      cellProps: {
        accessor: 'contact',
        sortable: false,
      },
    },
    {
      cellProps: {
        accessor: 'info',
        sortable: false,
        className: cn('rt-th-info', { 'sticky-column sticky-column_right': !isMobile }),
        headerClassName: cn('rt-th-info', { 'sticky-column sticky-column_right': !isMobile }),
        minWidth: 254,
      },
    },
    {
      cellProps: {
        accessor: 'actions',
        sortable: false,
        width: 40,
        headerClassName: 'rt-th-actions sticky-column sticky-column_right',
        className: 'rt-th-actions sticky-column sticky-column_right',
      },
    },
  ];

  const computeTableProps = useCallback(
    (projects = []) => {
      return {
        data: projects,
        columns: columns,
        className: 'admin_table projects_table',
        tableSettings: {
          hasHeader: true,
          pageSize: PROJECTS_PER_PAGE,
        },
      };
    },
    [columns],
  );

  const onDeleteProjectMutationSuccess = useCallback((data) => {
    if (data.deleteProject?.deletedCount) {
      toast('Selected projects was deleted', {
        ...toastParams,
        type: toast.TYPE.SUCCESS,
      });
      setSelected([]);
    } else {
      handleError({ message: `Selected projects can't be deleted` });
    }
  }, []);

  const [deleteProjectMutation] = useMutation(mutationDeleteProject, {
    onCompleted: onDeleteProjectMutationSuccess,
    onError: handleError,
  });

  const filterButtonsByPermission = (buttonsArray) =>
    buttonsArray.filter((item) => {
      if (item.id === 'delete' && !isPermission([delete_project])) return false;
      else if (item.id === 'edit' && !isPermission([change_project])) return false;
      return true;
    });

  const buttons = [
    {
      btnType: 'tertiary',
      id: 'shared',
      title: 'Shared',
      svg: <ShareSVG />,
      fun: () => console.log('Shared', selected),
    },
    { btnType: 'tertiary', id: 'edit', title: 'Edit', svg: <EditSVG />, fun: () => console.log('Edit', selected) },
    { btnType: 'tertiary', id: 'print', title: 'Print', svg: <PrintSVG />, fun: () => console.log('Print', selected) },
    {
      btnType: 'danger',
      id: 'delete',
      title: 'Delete',
      svg: <TrashSVG />,
      fun: () => {
        deleteProjectMutation({
          variables: {
            ids: selected,
          },
        });
      },
    },
  ];

  const getTableProps = ({ page, pageSize, sortedData }) => {
    const sortedProjectsIds = sortedData.map((data) => +data.select?.title.props.value || null);

    if (
      JSON.stringify(sortedProjectsIds) !== JSON.stringify(currentProjectsOrder) &&
      !sortedProjectsIds.some((id) => id === null)
    ) {
      setCurrentProjectsOrder(sortedProjectsIds);
    }

    if (currentPage !== page || pageSize !== currentPageSize) {
      setCurrentPage(page);
      setCurrentPageSize(pageSize);
      setSelected([]);
    }
    return {};
  };

  return (
    <>
      {loading && <LottieTable dataTestId="loadings_projects" />}
      <Table
        getTableProps={getTableProps}
        key="projects"
        {...computeTableProps(mapProjectsDataToRows(renderedProjects))}
        watchForResetPage={isChanged && projectsFilter}
        watchForTabChange={projectsFilter.status}
        stickyColumn={StickyColumnType.BOTH}
      />
      {!!selected.length && (
        <ActionPopup
          buttons={filterButtonsByPermission(buttons)}
          selected={selected}
          handleCleanSelects={handleCleanSelects}
        />
      )}
    </>
  );
};

export default ProjectsTable;
